import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'

// Ecran de calcul du Diagnostic : le logo R se compose comme au Tetris,
// puis se fige au noir du logo. Remplace le CircularProgress generique
// (cf review-valentin-nawal/ECARTS-THELAB.md, ecart 8, valide par Hicham
// sur la page animation-diag/index.html).
//
// - Le R est smallLogo.svg pixellise sur la maille du site (cadrillageBGCard
//   fait des carres de 33px, trait #E7E7E7), en 11 colonnes x 10 rangees.
// - Les couleurs sont les cinq carres de risinguparis.com, convertis en sRGB.
// - Rien n'est aleatoire : le R est decoupe en morceaux d'au plus 4 carres,
//   chacun tombe a sa place. L'assemblage est identique a chaque passage.
// - prefers-reduced-motion : pas de chute, le R s'affiche directement.

const R = [
  [1,1,1,1,1,1,1,1,1,0,0],
  [1,1,1,1,1,1,1,1,1,1,0],
  [1,1,1,1,1,1,1,1,1,1,0],
  [1,1,1,0,0,0,0,1,1,1,0],
  [1,1,1,0,1,1,1,1,0,0,0],
  [1,1,1,0,1,1,1,1,0,0,0],
  [1,1,1,0,1,1,1,1,1,0,0],
  [1,1,1,1,0,0,1,1,1,0,0],
  [1,1,1,1,0,0,1,1,1,1,0],
  [0,1,1,1,0,0,0,1,1,1,0],
]
/* exportee pour le tampon du bilan soft skills (Bilan/modeleBilan.js,
   07/10/2026) : un seul R pixel dans la plateforme */
export const GRILLE_R_PIXEL = R
const COL = R[0].length
const HAUT_R = R.length
const CIEL = 3                       // rangees de chute visibles au-dessus du R
const RANG = HAUT_R + CIEL
const NOIR_LOGO = "#0A0A0B"          // le noir de smallLogo.svg
const COULEURS = ["#FED030", "#167EFA", "#52D768", "#FC3140", "#8338EC"]

// Decoupe le R en morceaux d'au plus 4 carres, du bas vers le haut, pour que
// chaque morceau puisse se poser sur ce qui est deja la.
const decouperLeR = () => {
  const pris = R.map((l) => l.map(() => false))
  const morceaux = []
  for (let r = HAUT_R - 1; r >= 0; r--) {
    for (let c = 0; c < COL; c++) {
      if (!R[r][c] || pris[r][c]) continue
      const groupe = [[r, c]]
      pris[r][c] = true
      while (groupe.length < 4) {
        let ajout = null
        for (let g = 0; g < groupe.length && !ajout; g++) {
          const [gr, gc] = groupe[g]
          const voisins = [[gr, gc + 1], [gr - 1, gc], [gr, gc - 1]]
          for (let v = 0; v < voisins.length; v++) {
            const [nr, nc] = voisins[v]
            if (nr >= 0 && nr < HAUT_R && nc >= 0 && nc < COL && R[nr][nc] && !pris[nr][nc]) {
              ajout = [nr, nc]
              break
            }
          }
        }
        if (!ajout) break
        pris[ajout[0]][ajout[1]] = true
        groupe.push(ajout)
      }
      morceaux.push({ cases: groupe, couleur: COULEURS[morceaux.length % COULEURS.length] })
    }
  }
  return morceaux
}
const MORCEAUX = decouperLeR()

const ChargementPixelR = ({ maille = [18, 24, 24, 28] }) => {
  // grille affichee : null = vide, sinon couleur du carre
  const grilleRef = useRef(Array.from({ length: RANG }, () => Array(COL).fill(null)))
  const [, setTic] = useState(0)
  const [fige, setFige] = useState(false)

  useEffect(() => {
    let sobre = false
    try { sobre = window.matchMedia("(prefers-reduced-motion: reduce)").matches } catch (e) {}

    const poserToutLeR = () => {
      MORCEAUX.forEach((m) => m.cases.forEach(([r, c]) => {
        grilleRef.current[r + CIEL][c] = m.couleur
      }))
      setTic((t) => t + 1)
    }

    if (sobre) {
      poserToutLeR()
      setFige(true)
      return undefined
    }

    let suivant = 0
    let envol = []
    let compteur = 0
    const minuterie = setInterval(() => {
      // un nouveau morceau tous les 2 tics
      if (compteur % 2 === 0 && suivant < MORCEAUX.length) {
        const m = MORCEAUX[suivant++]
        const base = Math.min(...m.cases.map(([r]) => r))
        const haut = Math.max(...m.cases.map(([r]) => r))
        envol.push({ m, base, y: -(haut - base + 1), cible: base + CIEL })
      }
      compteur++

      envol = envol.filter((p) => {
        p.m.cases.forEach(([r, c]) => {
          const lig = p.y + (r - p.base)
          if (lig >= 0 && lig < RANG && grilleRef.current[lig][c] === p.m.couleur) {
            grilleRef.current[lig][c] = null
          }
        })
        p.y++
        const arrive = p.y >= p.cible
        p.m.cases.forEach(([r, c]) => {
          const lig = arrive ? r + CIEL : p.y + (r - p.base)
          if (lig >= 0 && lig < RANG) grilleRef.current[lig][c] = p.m.couleur
        })
        return !arrive
      })
      setTic((t) => t + 1)

      if (suivant >= MORCEAUX.length && envol.length === 0) {
        clearInterval(minuterie)
        poserToutLeR()
        setFige(true)      // bascule au noir du logo, geree en CSS (transition)
      }
    }, 24)

    return () => clearInterval(minuterie)
  }, [])

  return (
    <Box sx={{
      display:"grid",
      gridTemplateColumns:[
        `repeat(${COL}, ${maille[0]}px)`,
        `repeat(${COL}, ${maille[1]}px)`,
        `repeat(${COL}, ${maille[2]}px)`,
        `repeat(${COL}, ${maille[3]}px)`,
      ],
      justifyContent:"center",
      userSelect:"none",
      pointerEvents:"none",
    }}>
      {grilleRef.current.map((ligne, r) => ligne.map((coul, c) => (
        <Box
          key={r + "-" + c}
          sx={{
            width:[maille[0]+"px", maille[1]+"px", maille[2]+"px", maille[3]+"px"],
            height:[maille[0]+"px", maille[1]+"px", maille[2]+"px", maille[3]+"px"],
            // la maille du site : trait fin sur les cases vides uniquement
            border: coul ? "1px solid transparent" : "1px solid #E7E7E7",
            marginTop:"-1px",
            marginLeft:"-1px",
            backgroundColor: coul ? (fige ? NOIR_LOGO : coul) : "transparent",
            // du bas vers le haut : les rangees basses basculent d'abord
            transition: fige ? `background-color .4s ease-out ${(RANG - r) * 0.04}s` : "none",
          }}
        />
      )))}
    </Box>
  )
}

export default ChargementPixelR
